import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, PartyPopper, Users2, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

export default function Banquet() {
    const { activeHotelId } = useAuth();
    const [halls, setHalls] = useState([]);
    const [events, setEvents] = useState([]);
    const [openHall, setOpenHall] = useState(false);
    const [openEvent, setOpenEvent] = useState(false);
    const [hallForm, setHallForm] = useState({ name: "", capacity: 100, hourly_rate: 5000 });
    const [evForm, setEvForm] = useState({ hall_id: "", event_type: "marriage", client_name: "", client_phone: "", date: "", start_time: "18:00", end_time: "23:00", guest_count: 50, package: "silver", food_package_amount: 0, decoration_amount: 0, notes: "" });

    const load = async () => {
        if (!activeHotelId) return;
        const [h, e] = await Promise.all([
            api.get(`/banquet/halls?hotel_id=${activeHotelId}`),
            api.get(`/banquet/events?hotel_id=${activeHotelId}`),
        ]);
        setHalls(h.data); setEvents(e.data);
    };
    useEffect(() => { load(); }, [activeHotelId]);

    const submitHall = async () => {
        try {
            await api.post("/banquet/halls", { ...hallForm, hotel_id: activeHotelId, capacity: Number(hallForm.capacity), hourly_rate: Number(hallForm.hourly_rate) });
            toast.success("Hall created"); setOpenHall(false); setHallForm({ name: "", capacity: 100, hourly_rate: 5000 }); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const submitEvent = async () => {
        try {
            await api.post("/banquet/events", {
                ...evForm, hotel_id: activeHotelId,
                guest_count: Number(evForm.guest_count),
                food_package_amount: Number(evForm.food_package_amount),
                decoration_amount: Number(evForm.decoration_amount),
            });
            toast.success("Event booked"); setOpenEvent(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Banquet & Events" subtitle={`${halls.length} halls · ${events.length} events`}>
            <Tabs defaultValue="events">
                <TabsList>
                    <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
                    <TabsTrigger value="halls" data-testid="tab-halls">Halls</TabsTrigger>
                </TabsList>
                <TabsContent value="events" className="mt-4">
                    <div className="flex justify-end mb-3">
                        <Dialog open={openEvent} onOpenChange={setOpenEvent}>
                            <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="event-add-btn"><Plus className="w-4 h-4 mr-1" /> Event</Button></DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader><DialogTitle>Book Event</DialogTitle></DialogHeader>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="text-xs uppercase tracking-wider">Hall</Label>
                                        <Select value={evForm.hall_id} onValueChange={v => setEvForm({ ...evForm, hall_id: v })}>
                                            <SelectTrigger data-testid="ev-hall"><SelectValue placeholder="Choose hall" /></SelectTrigger>
                                            <SelectContent>{halls.map(h => <SelectItem key={h.id} value={h.id}>{h.name} · cap {h.capacity}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Event Type</Label>
                                        <Select value={evForm.event_type} onValueChange={v => setEvForm({ ...evForm, event_type: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>{["marriage", "conference", "meeting", "birthday", "corporate"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Client Name</Label><Input value={evForm.client_name} onChange={e => setEvForm({ ...evForm, client_name: e.target.value })} data-testid="ev-client" /></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Client Phone</Label><Input value={evForm.client_phone} onChange={e => setEvForm({ ...evForm, client_phone: e.target.value })} /></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Date</Label><Input type="date" value={evForm.date} onChange={e => setEvForm({ ...evForm, date: e.target.value })} data-testid="ev-date" /></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Guests</Label><Input type="number" value={evForm.guest_count} onChange={e => setEvForm({ ...evForm, guest_count: e.target.value })} /></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Food Package (₹)</Label><Input type="number" value={evForm.food_package_amount} onChange={e => setEvForm({ ...evForm, food_package_amount: e.target.value })} /></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Decoration (₹)</Label><Input type="number" value={evForm.decoration_amount} onChange={e => setEvForm({ ...evForm, decoration_amount: e.target.value })} /></div>
                                </div>
                                <DialogFooter><Button onClick={submitEvent} disabled={!evForm.hall_id || !evForm.client_name || !evForm.date} className="bg-[#0A1C3E]" data-testid="ev-submit">Book</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {events.map(e => (
                            <Card key={e.id} className="p-4 border border-slate-200 rounded-md shadow-none" data-testid={`event-${e.id}`}>
                                <div className="flex justify-between items-start">
                                    <PartyPopper className="w-4 h-4 text-[#C5B08A]" />
                                    <span className="status-badge bg-slate-100 text-slate-700 border-slate-200">{e.status}</span>
                                </div>
                                <div className="mt-2 font-semibold text-slate-900">{e.client_name}</div>
                                <div className="text-xs text-slate-500 uppercase tracking-wider">{e.event_type}</div>
                                <div className="text-xs text-slate-600 mt-2 flex items-center gap-1"><CalIcon className="w-3 h-3" /> {e.date} · {e.start_time}–{e.end_time}</div>
                                <div className="text-xs text-slate-600 flex items-center gap-1"><Users2 className="w-3 h-3" /> {e.guest_count} guests</div>
                                <div className="mt-3 pt-3 border-t border-slate-100 flex justify-between items-center">
                                    <span className="text-xs text-slate-500">Total</span>
                                    <span className="font-mono font-semibold">₹{Number(e.total).toLocaleString()}</span>
                                </div>
                            </Card>
                        ))}
                        {events.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">No events booked</div>}
                    </div>
                </TabsContent>
                <TabsContent value="halls" className="mt-4">
                    <div className="flex justify-end mb-3">
                        <Dialog open={openHall} onOpenChange={setOpenHall}>
                            <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="hall-add-btn"><Plus className="w-4 h-4 mr-1" /> Hall</Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>New Hall</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div><Label className="text-xs uppercase tracking-wider">Name</Label><Input value={hallForm.name} onChange={e => setHallForm({ ...hallForm, name: e.target.value })} data-testid="hall-name" /></div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><Label className="text-xs uppercase tracking-wider">Capacity</Label><Input type="number" value={hallForm.capacity} onChange={e => setHallForm({ ...hallForm, capacity: e.target.value })} /></div>
                                        <div><Label className="text-xs uppercase tracking-wider">Hourly Rate (₹)</Label><Input type="number" value={hallForm.hourly_rate} onChange={e => setHallForm({ ...hallForm, hourly_rate: e.target.value })} /></div>
                                    </div>
                                </div>
                                <DialogFooter><Button onClick={submitHall} disabled={!hallForm.name} className="bg-[#0A1C3E]" data-testid="hall-submit">Create</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {halls.map(h => (
                            <Card key={h.id} className="p-4 border border-slate-200 rounded-md shadow-none">
                                <div className="font-semibold">{h.name}</div>
                                <div className="text-xs text-slate-500">Capacity: {h.capacity}</div>
                                <div className="mt-2 text-sm font-mono">₹{Number(h.hourly_rate).toLocaleString()}/hr</div>
                            </Card>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </AppShell>
    );
}
